import { PaymentType } from '@/models/app-config/behavior/types'
import { trimCDATA } from '@/util/cdata'
import { log } from '@spa/log'
import { OrderPurchaseResult } from '@spa/services/order'
import { Either, left, right } from 'fp-ts/lib/Either'

interface RedirectHtmlRenderer {
  /**
   * @return boolean リダイレクトされたらtrue/それ以外はfalse
   */
  render(): boolean
}
interface RedirectScriptFireStrategy {
  /**
   * @return {Either} 成功時はRight<null>を、失敗時はLeft<Error>を返す
   */
  fireRedirectScript(): Either<Error, null>
}

export const createRedirectHtmlRenderer = (
  paymentType: PaymentType,
  results: InnerOrderResult[]
): RedirectHtmlRenderer => {
  const browserBackUrl = '/external_payment/common/browser_back'

  switch (paymentType) {
    case 'carrier':
      return new PaymentRenderer(results, browserBackUrl, fireScriptViaLoadEvent)
    case 'webmoney':
    /**
     * 3Dセキュア用に返却されるhtmlは、本来
     * 「HTMLを読み込むと、<script>タグ内部で登録された関数がloadイベントをトリガーに自動実行され、formの内容がsubmitされる」
     * という仕組みになっている.
     * 具体的には以下のようなhtmlが提供される.
     * ```html
     * <script>
     *   function xxx() { }
     * </script>
     * <form onload="xxx()">
     * </form>
     * ```
     *
     * しかし以下の理由から、ここではあえて（あるいはやむを得ず）script内部を無視して、直接formに対してsubmitを行っている.
     * - このモジュールの実装が「innerHtmlを直接更新して、リダイレクト処理は別途JSからなんらかの方法で発火させる」という前提を強く取っている
     * - バンドルされたJSは"use strict"が有効になった状態となるので、evalでscriptのtextを実行しても内容がグローバルに影響しない
     *   - つまり、script内部で関数が定義されていても、その関数はグローバル空間には作成されない
     *   - 一方、外部提供のHTMLは<form onload="xxx()">の形でscriptタグ外関数を参照しようとしている
     *     = グローバル空間に関数があることが前提になっている（これ自体はHTML標準なので正当）
     *   - 上記より、evalを使ったとしても参照エラーで処理実行に失敗する
     *
     * 上記の通り、「返却されるHTMLの詳細に強く依存した実装」となっている点に注意.
     * 例えば「submitの前後で行われるべき固有の処理がscript内部の関数で定義されている」といったHTMLが提供された場合、
     * 現状(2023/12/13)時点の設計ではおそらく対処不能.
     *
     * 本来的には、こうした外部ページ遷移が必要 & 外部から提供されるHTMLの利用が必要なケースでは、
     * 購入処理および画面遷移をクライアントサイドのJSで完結させるのではなく、
     * 「標準HTMLのform + submitによるページ遷移を伴うリクエスト送信」といった古典的手法の方が望ましいと思われる.
     *
     * ただしほぼ新規実装となるため、 https://github.com/my-color/growthpalette-front/issues/7192 の対応時点では
     * 一旦既存実装にそのまま乗る形での対処とする.
     * @see https://github.com/my-color/growthpalette-front/issues/7192
     * @see https://my-color.esa.io/posts/965
     */
    case 'creditcard3d':
      return new PaymentRenderer(results, browserBackUrl, submitForm)
    default:
      return new NothingRenderer()
  }
}

class PaymentRenderer implements RedirectHtmlRenderer {
  constructor(
    private readonly results: InnerOrderResult[],
    private readonly browserBackUrl: string,
    private readonly redirectScriptFireStrategy: RedirectScriptFireStrategy
  ) {
  }

  render(): boolean {
    const result = this.getOrderResultWithRedirectView(this.results)
    if (result === null) {
      return false
    }

    const htmlElement = document.querySelector('html')
    if (!htmlElement) {
      log.error('Redirect HTML rendering error: cannot find <html> element.')

      return false
    }

    // 事前にredirectHTMLの存在確認済み
    htmlElement.innerHTML = trimCDATA(result.redirectHtml!)

    /**
     * 注文後のためここでは確実にidが存在するはずだが、Orderの型定義上、idがundefinedの可能性がある。
     * OrderPurchaseResultに別途salesOrderIdという必須フィールドを設けることも可能だが、
     * 既存実装への影響を抑えるため対応は見送る。
     */
    const cancelRequest = result.id
      ? `${this.browserBackUrl}?order_id=${result.id}`
      : this.browserBackUrl
    history.pushState(null, '', cancelRequest)

    return this.redirectScriptFireStrategy.fireRedirectScript().fold(
      (e: Error) => {
        log.error(e.message)

        return false
      },
      () => true
    )
  }

  private getOrderResultWithRedirectView(
    results: InnerOrderResult[]
  ): InnerOrderResult | null {
    const filteredResults = results.filter(result => result.redirectHtml !== undefined)
    if (filteredResults.length === 0) {
      return null
    }

    if (filteredResults.length > 1) {
      log.error('Two or more redirectHtml found. All but the first element will be ignored.')
    }

    return filteredResults[0]
  }
}

/**
 * なにもしないrenderer
 * HTML出力が不要な場合に利用する
 */
class NothingRenderer implements RedirectHtmlRenderer {
  render(): boolean {
    return false
  }
}

const fireScriptViaLoadEvent: RedirectScriptFireStrategy = {
  fireRedirectScript(): Either<Error, null> {
    const event = document.createEvent('HTMLEvents')
    event.initEvent('load', false, true)
    window.dispatchEvent(event)

    return right(null)
  },
}
const submitForm: RedirectScriptFireStrategy = {
  fireRedirectScript(): Either<Error, null> {
    // `form`タグは必ず一つだけ存在する想定だが
    // getElementsByTagNameは配列を返すので要素数のチェックを行っている
    // https://github.com/my-color/growthpalette-front/issues/4784
    const forms = document.getElementsByTagName('form')
    if (forms.length === 0) {
      return left(new Error('Auto redirect form could not be found.'))
    }

    if (forms.length > 1) {
      log.error('Two or more form tags were found. All but the first element will be ignored.')
    }

    forms[0].submit()

    return right(null)
  },
}

/**
 * 内部的に利用される注文結果を表すインタフェース
 * 通常の注文と再注文で結果のオブジェクトが異なるため、
 * リダイレクトに必要な情報のみに限定した最小の依存情報としている。
 */
interface InnerOrderResult {
  id?: string
  redirectHtml?: string
}

/**
 * 通常の注文（初回注文）の結果オブジェクトOrderPurchaseResultからInnerOrderResultへ変換する
 */
export function convertFromOrderPurchaseResult(result: OrderPurchaseResult): InnerOrderResult {
  return {
    id: result.order.id,
    redirectHtml: result.redirectHtml,
  }
}
